import { ASSESSMENT_TYPES } from '@/config/constants'

/**
 * Pure lookup helpers for survey-visit / assessment display labels.
 *
 * `staticChoices` is the Vuex `state.app.staticChoices` map. Each entry
 * is an array of `{ value, label, text, uiLabel }` objects (see
 * `src/store/base/actions.js::fetchStaticChoices`). `uiLabel` is the
 * presentation-layer copy; `label` is the BE admin label which may
 * diverge (e.g. ACCREDITATION → label "Accreditation" but ui_label
 * "Survey").
 *
 * The helpers take `staticChoices` explicitly so they stay pure and
 * trivially testable. Callers typically do:
 *   getSurveyVisitTypeLabel(this.staticChoices, row.surveyVisit.type)
 */

const lookupUiLabel = (staticChoices, choicesKey, value, fallback) => {
    if (!value) return fallback
    const entries = (staticChoices && staticChoices[choicesKey]) || []
    const match = entries.find((e) => e && e.value === value)
    return match ? (match.uiLabel || match.label) : fallback
}

/**
 * Resolve the UI label for a SurveyVisit.type value (e.g. 'ACCREDITATION').
 * Returns 'none' when the value is not configured in staticChoices.
 */
const getSurveyVisitTypeLabel = (staticChoices, type) =>
    lookupUiLabel(staticChoices, 'survey_visit.type', type, 'none')

/**
 * Resolve the UI label for an Assessment.type value (e.g. 'PRESURVEY').
 * Returns '' when the value is not configured in staticChoices.
 */
const getAssessmentTypeLabel = (staticChoices, type) =>
    lookupUiLabel(staticChoices, 'assessment.type', type, '')

/**
 * Resolve the display label for a "row" that is polymorphic across the
 * assessment-list views: either a PRESURVEY Assessment (uses
 * `row.type`) or any other Assessment whose linked SurveyVisit drives
 * the label (uses `row.surveyVisit.type`). Returns '' when the row is
 * missing or its type is not configured.
 */
const getRowTypeLabel = (staticChoices, row) => {
    if (!row) return ''
    if (row.type === ASSESSMENT_TYPES.PreSurvey) {
        return getAssessmentTypeLabel(staticChoices, row.type)
    }
    const svType = row.surveyVisit && row.surveyVisit.type
    return getSurveyVisitTypeLabel(staticChoices, svType)
}

export {
    getSurveyVisitTypeLabel,
    getAssessmentTypeLabel,
    getRowTypeLabel,
}
